import { useMemo, useState } from 'react';
import { XMarkIcon as CloseIcon } from '@heroicons/react/24/outline';
import { useCurrentRefinements } from 'components/commercetools-ui/organisms/product/product-list-ssr/local-store';
import Accordion from 'components/commercetools-ui/atoms/accordion';
import Drawer from 'components/commercetools-ui/atoms/drawer';
import { useFormat } from 'helpers/hooks/useFormat';
import { useProductList } from '../../context';
import useDynamicFacets from '../../hooks/useDynamicFacets';
import { FacetConfiguration } from '../../types';
import DesktopSortFacets from '../desktop-facets/sort-facets';
import { useParams } from 'next/navigation';
import usePath from 'helpers/hooks/usePath';
import { localeMapping } from 'helpers/constants/localeMapping';

interface Props {
  facetsConfiguration: Record<string, FacetConfiguration>;
}
const MobileFacets: React.FC<Props> = ({ facetsConfiguration }) => {
  const { locale } = useParams();
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const { removeAllRefinements } = useProductList();

  const  Path  = usePath();
  const { items } = useCurrentRefinements();
  const localeKey = localeMapping[locale];
  
  const [isOpen, setIsOpen] = useState(false);

  const accordionClassNames = useMemo(
    () => ({
      root: 'py-20 mx-24',
      icon: 'stroke-secondary-black w-20 stroke-2',
      border: 'border-b border-[#808080]',
      button: 'text-16 text-primary-black',
    }),
    [],
  );

  const isOnClarkPath = Path.path.includes('/brands'); 
  let isSanitarywareSelected = false;
  if (isOnClarkPath) {
    isSanitarywareSelected = items.some(
      (item) =>
        (item.attribute === `categories.${localeKey}.lvl0` &&
         item.refinements.some(
           (refinement) => 
             refinement.value === 'Sanitaryware' || 
             refinement.value === 'Tapware'
         )) ||
        (item.attribute === 'variants.attributes.colour' &&
         item.refinements.some(
           (refinement) => refinement.value === 'Anthracite Grey' ||
             refinement.value === 'Black'||
           refinement.value === 'Brushed Brass'
          )) ||
        (item.attribute === 'variants.attributes.item-material' &&
            item.refinements.some(
              (refinement) => refinement.value === 'Brass' 
          )) ||
          (item.attribute === 'attributes.range' &&
            item.refinements.some(
              (refinement) => refinement.value === 'Acqua' ||
                refinement.value === 'Aire' || 
                refinement.value === 'Aura' ||
                refinement.value === 'Urbane II' ||
                refinement.value === 'Urbane' ||
                refinement.value === 'Tribute' ||
                refinement.value === 'Trident' ||
                refinement.value === 'Uniset II' ||
                refinement.value === 'Universal' ||
                refinement.value === 'Track' ||
                refinement.value === 'Torres' 
        ))
    );

    Object.keys(facetsConfiguration).forEach((key) => {
      const facet = facetsConfiguration[key];
      if (key === 'variants.attributes.wels-with-flow-rate-flush-volume-1') {
        facet.label = isSanitarywareSelected ? 'WELS Rating' : 'WELS Rating (Shower)';
      }
    });
  }



  //This code is for Mobile Facet
  // Checking Facets "IsAS14281Compliant", "IsAS14281Ambulant" & "IsNZ41121Compliant"
  // In PLP page 
  // If locale is NZ then we will show IsNZ41121Compliant    and    if locale is AU then we will show "IsAS14281Compliant" & "IsAS14281Ambulant"

  // Filter out facets based on AU and NZ
  const filteredFacetsConfiguration = Object.fromEntries(
    Object.entries(facetsConfiguration).filter(([key]) => {
      if (locale === 'nz') {
        return key !== 'variants.attributes.IsAS14281Compliant' && key !== 'variants.attributes.IsAS14281Ambulant';
      }
      else if (locale === 'au') {
        return key !== 'variants.attributes.IsNZ41121Compliant';
      }
      return true;
    })
  );

  const facets = useDynamicFacets({
    configuration: filteredFacetsConfiguration,
    render: ({ attribute, Component }) => (
      <Accordion
        className={`${accordionClassNames.root} ${accordionClassNames.border}`}
        buttonClassName={accordionClassNames.button}
        closedSectionTitle={filteredFacetsConfiguration[attribute].label}
        variant="arrow"
        iconClassName={accordionClassNames.icon}
      >
        <div className="py-16">{Component}</div>
      </Accordion>
    ),
  });

  return (
    <>
      <div className="flex items-center justify-between gap-4 lg:hidden">
        <div className="flex-1">
          <button
            className={`flex w-full cursor-pointer items-center justify-center gap-12  border border-[#2B2C39] ${isOpen?'bg-[#2B2C39] text-white': 'bg-inherit '} px-18 py-10 text-16 leading-[20px] transition `}
            onClick={() => setIsOpen(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="16" viewBox="0 0 18 16" fill="none" className={isOpen?'invert':''}>
              <path
                d="M16.0344 13.165V15.665H14.5268V13.165H13.0193V11.4984H17.5419V13.165H16.0344ZM3.97406 13.165V15.665H2.46652V13.165H0.958984V11.4984H5.4816V13.165H3.97406ZM8.49668 3.16504V0.665039H10.0042V3.16504H11.5118V4.83171H6.98914V3.16504H8.49668ZM8.49668 6.49837H10.0042V15.665H8.49668V6.49837ZM2.46652 9.83171V0.665039H3.97406V9.83171H2.46652ZM14.5268 9.83171V0.665039H16.0344V9.83171H14.5268Z"
                fill="#090909"
              />
            </svg>
            <span className="text-16 font-medium">
              {formatProductMessage({ id: 'filter', defaultMessage: 'Filters' })}
            </span>
          </button>
        </div>
        <div className="flex-1">
        <DesktopSortFacets/>
        </div>
      </div>

      <Drawer isOpen={isOpen} onClose={() => setIsOpen(false)} direction="bottom" className="h-2/3 bg-white">
        <div className="px-24 md:px-28 flex items-center justify-between border-b border-[#808080] py-16">
          <h3 className="text-32 font-medium text-[#333]">
            {' '}
            {formatProductMessage({ id: 'filters', defaultMessage: 'Filters' })}
          </h3>
          <div className="flex items-center gap-18">
            <span
              className="text-16 font-normal text-[#333] underline hover:cursor-pointer"
              onClick={removeAllRefinements}
            >
              {formatProductMessage({ id: 'clear.all', defaultMessage: 'Clear All' })}
            </span>

            <CloseIcon className="w-24 stroke-secondary-black" onClick={() => setIsOpen(false)} />
          </div>
        </div>
        <div className="grow overflow-auto">{facets}</div>
      </Drawer>
    </>
  );
};

export default MobileFacets;
